import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io } from "socket.io-client";

/**
 * SocketProvider
 *
 * Purpose:
 * - Create and manage a single Socket.IO connection for the entire React app (per browser tab).
 * - Expose convenient helpers for components to `emit`, `on`, `off`, and `subscribe` to events.
 * - Use JWT auth token when connecting (retrieved from localStorage by default — replace with your app auth getter).
 *
 * Usage:
 * - Wrap your app (or admin area) with <SocketProvider> at root level.
 * - Inside components call `const { socket, emit, on, off, subscribe, connected } = useSocket();`
 *
 * Notes:
 * - The provider attempts to connect once on mount. If no token is found the socket will not connect.
 * - If your app uses a different auth store (context, cookie, etc.) replace `getJwtToken()` implementation.
 * - Environment: Set REACT_APP_WS_URL to the socket server origin (e.g. http://localhost:4000). If absent, falls back to window.location.origin.
 */

// Context that holds socket helpers
const SocketContext = createContext(null);

/**
 * Helper to safely get token for socket auth.
 * Replace this with your app's centralized auth retrieval if you have one.
 */
function getJwtToken() {
  // Prefer an auth provider if available; fall back to localStorage for now
  if (typeof window === "undefined") return null;
  return (
    // Common keys used in this project
    localStorage.getItem("token") || localStorage.getItem("jwt") || null
  );
}

/**
 * SocketProvider component
 */
export const SocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const token = getJwtToken();
    if (!token) {
      // Do not connect if token is missing — prevents unauthorized joins and noisy errors
      console.warn(
        "SocketProvider: no JWT found. Socket will not connect. Provide a token in localStorage('token'|'jwt') or implement getJwtToken().",
      );
      return;
    }

    // const wsUrl = process.env.REACT_APP_WS_URL || window.location.origin;
    const wsUrl = "https://api.kifaytihealth.com";

    // const socket = io(wsUrl, {
    //   path: "/api2/socket.io",
    //   auth: { token: `Bearer ${token}` },
    //   transports: ["websocket"],
    //   autoConnect: true,
    //   reconnection: true,
    //   reconnectionAttempts: 1,
    //   reconnectionDelay: 1000,
    //   reconnectionDelayMax: 5000,
    //   timeout: 10000,
    // });

    const socket = io("wss://api.kifaytihealth.com", {
      path: "/api2/socket.io",
      auth: { token: `Bearer ${token}` },
      transports: ["websocket"],   // important
    });


    socketRef.current = socket;

    const onConnect = () => {
      setConnected(true);
      console.info("Socket connected:", socket.id);
    };
    const onDisconnect = (reason) => {
      setConnected(false);
      console.info("Socket disconnected:", reason);
    };
    const onConnectError = (err) => {
      console.error("Socket connect_error:", err?.message || err);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);

    // cleanup: remove all listeners and disconnect when provider unmounts
    return () => {
      if (socketRef.current) {
        try {
          socketRef.current.off("connect", onConnect);
          socketRef.current.off("disconnect", onDisconnect);
          socketRef.current.off("connect_error", onConnectError);
          socketRef.current.removeAllListeners();
          socketRef.current.disconnect();
        } catch (e) {
          // swallow any errors during cleanup
          console.warn("SocketProvider cleanup warning:", e);
        } finally {
          socketRef.current = null;
          setConnected(false);
        }
      }
    };
    // Intentionally run only once on mount. If you need to reconnect on token changes,
    // modify this effect to include a token dependency and handle re-init carefully.
  }, []);

  // Safe wrappers around socket operations
  const emit = (event, payload, ack) => {
    if (!socketRef.current) {
      console.warn("Socket emit attempted before socket initialized:", event);
      return;
    }
    try {
      socketRef.current.emit(event, payload, ack);
    } catch (err) {
      console.error("Socket emit error:", err);
    }
  };

  const on = (event, handler) => {
    if (!socketRef.current) {
      console.warn("Socket 'on' attempted before socket initialized:", event);
      return;
    }
    socketRef.current.on(event, handler);
  };

  const off = (event, handler) => {
    if (!socketRef.current) return;
    // if handler is provided remove that handler otherwise remove all listeners for event
    if (handler) socketRef.current.off(event, handler);
    else socketRef.current.off(event);
  };

  /**
   * subscribe(event, handler)
   * - Registers the handler and returns an unsubscribe function for convenience.
   * - Useful for React effects to auto-cleanup.
   */
  const subscribe = (event, handler) => {
    on(event, handler);
    return () => off(event, handler);
  };

  const value = {
    socket: socketRef.current,
    emit,
    on,
    off,
    subscribe,
    connected,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

/**
 * useSocket hook
 * - Convenience consumer that throws a helpful error when used outside provider.
 */
export const useSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) {
    throw new Error(
      "useSocket must be used within a SocketProvider. Wrap your app with <SocketProvider>.",
    );
  }
  return ctx;
};

export default SocketProvider;
